import { useEffect, useState } from 'react';
import { GridColDef, GridPaginationModel } from '@mui/x-data-grid';
import { useSelector } from 'react-redux';
import { RootState } from '../../../../../redux/store';

import DateTimeFormat from '../../../../components/dateTimeFormat';
import { TableTooltip } from '../../../../components/general/table';

import {
    Quotation,
    QuotationFilter,
} from '../../../../../shared/types/modules/quotation';
import { asyncGetQuotations } from '../../../../../services/quotation.service';
import { isOnlyViewProfile } from '../../../../../shared/types/profiles';
import { ISelect } from '../../../../../shared/types/shared';

import { QuotationListActions } from './actions';

export const useQuotationList = (filters?: QuotationFilter) => {
    const { logUser } = useSelector((state: RootState) => state.general);
    const { type } = logUser || {};

    const [loading, setLoading] = useState(false);
    const [rows, setRows] = useState<Quotation[]>([]);
    const [page, setPage] = useState<number>(0);
    const [pageSize, setPageSize] = useState<number>(10);
    const [total, setTotal] = useState<number>(0);
    const [finalFilters, setFinalFilters] = useState<QuotationFilter>({});

    const paginator = (model: GridPaginationModel) => {
        if (model.page !== page) {
            setPage(model.page);
        } else if (model.pageSize !== pageSize) {
            setPageSize(model.pageSize);
        }
    };

    const columns: GridColDef<Quotation>[] = [];

    if (!isOnlyViewProfile(type)) {
        columns.push({
            field: 'actions',
            headerName: 'Acciones',
            width: 100,
            sortable: false,
            disableColumnMenu: true,
            filterable: false,
            align: 'center',
            headerAlign: 'center',
            renderCell: (row) => <QuotationListActions id={row.id} />,
        });
    }

    columns.push(
        {
            field: 'typeDocument',
            headerName: 'Tipo de documento',
            minWidth: 120,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={`${(
                        row.row.account?.typeDocument as ISelect
                    )?.name.toString()}`}
                />
            ),
        },
        {
            field: 'document',
            headerName: 'Identificación',
            minWidth: 120,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip value={`${row.row.account?.document}`} />
            ),
        },
        {
            field: 'name',
            headerName: 'Nombre / Razón social',
            minWidth: 120,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip value={`${row.row.account?.name}`} />
            ),
        },
        {
            field: 'callId',
            headerName: 'Call ID',
            minWidth: 120,
            flex: 0.6,
            renderCell: (row) => <TableTooltip value={`${row.row.callId}`} />,
        },
        {
            field: 'typeQuotation',
            headerName: 'Tipo de cotización',
            minWidth: 120,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={`${
                        (row.row.typeQuotation as ISelect)?.name.toString() ||
                        ''
                    }`}
                />
            ),
        },
        {
            field: 'bouquet',
            headerName: 'Ramo',
            minWidth: 120,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={`${(row.row.bouquet as ISelect)?.name.toString()}`}
                />
            ),
        },
        {
            field: 'status',
            headerName: 'Estado',
            minWidth: 120,
            flex: 0.6,
            renderCell: (row) => (
                <TableTooltip
                    value={`${
                        (row.row.status as ISelect)?.name.toString() || ''
                    }`}
                />
            ),
        },
        {
            field: 'dateCreate',
            headerName: 'Fecha de creación',
            minWidth: 120,
            flex: 0.4,
            renderCell: (row) => <DateTimeFormat dateAt={row.row.created_at} />,
        }
    );

    useEffect(() => {
        setPage(0);
        setFinalFilters(filters || {});
    }, [filters]);

    useEffect(() => {
        setLoading(true);
        asyncGetQuotations(finalFilters, page, pageSize)
            .then((res) => {
                setRows(res.items);
                setTotal(res.length);
            })
            .finally(() => setLoading(false));
    }, [page, pageSize, finalFilters]);

    return {
        columns,
        rows,
        page,
        pageSize,
        paginator,
        loading,
        total,
    };
};
